"use client";

import {
  EmployersHeader,
  sideMenuToggleButtonId,
} from "@/app/employers/[officeId]/(use-shared-layout)/EmployersHeader";
import {
  EmployersSideMenu,
  sideMenuId,
} from "@/app/employers/[officeId]/(use-shared-layout)/EmployersSideMenu";
import { Overlay } from "@/components/ui/Overlay";
import { cn } from "@/lib/utils";
import { ComponentProps, useEffect, useState, type ReactNode } from "react";

export const EmployersSharedLayout = ({
  currentOffice,
  availableOffices,
  employerName,
  insurerNumber,
  children,
  params,
}: {
  currentOffice: ComponentProps<typeof EmployersHeader>["currentOffice"];
  availableOffices: ComponentProps<typeof EmployersHeader>["availableOffices"];
  employerName: string;
  insurerNumber: string;
  children: ReactNode;
  params: { officeId: string };
}) => {
  const [sideMenuOpened, setSideMenuOpened] = useState(false);
  const toggleSideMenuOpened = () => setSideMenuOpened((p) => !p);
  const closeSideMenu = () => setSideMenuOpened(false);

  useEffect(() => {
    // 遷移時などにサイドメニューが閉じないので下記の条件の要素をクリックした場合にはサイドメニューを閉じる
    const closeSideMenuOnClickOutside = (e: MouseEvent) => {
      const clickedEl = e.target as HTMLElement | null;
      if (
        // aタグは常に閉じる
        clickedEl?.closest("a") ||
        // サイドメニューのトグルボタンは要素自身でハンドリングするので不要
        (!clickedEl?.closest(`#${sideMenuToggleButtonId}`) &&
          // サイドメニューの中身をクリックした場合は閉じない、サイドメニュー内のリンクの場合は先にaタグの条件に引っかかるので閉じる
          !clickedEl?.closest(`#${sideMenuId}`))
      ) {
        // Safariでは新規タブに切り替えた後にサイドメニューが開かなくなるので、targetがある場合は閉じない
        if (clickedEl?.closest("a")?.target) {
          return;
        }
        closeSideMenu();
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // タブがアクティブになったときにサイドメニューを閉じる
        closeSideMenu();
      }
    };

    document.addEventListener("click", closeSideMenuOnClickOutside);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("click", closeSideMenuOnClickOutside);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return (
    <div
      className={cn(
        "flex min-h-svh flex-row",
        // サイドメニューが開いているときはスクロールをブロック
        sideMenuOpened && "max-h-svh overflow-x-hidden",
      )}
    >
      <EmployersSideMenu
        officeName={currentOffice.name}
        officeCode={currentOffice.officeCode}
        insurerNumber={insurerNumber}
        sideMenuOpened={sideMenuOpened}
        params={params}
      />
      <div className="w-full">
        <EmployersHeader
          currentOffice={currentOffice}
          availableOffices={availableOffices}
          employerName={employerName}
          sideMenuOpened={sideMenuOpened}
          toggleSideMenuOpened={() => toggleSideMenuOpened()}
          params={params}
        />
        <Overlay
          className={cn(
            "z-10 hidden opacity-0 transition-opacity",
            sideMenuOpened && "block opacity-30",
          )}
          onClick={() => closeSideMenu()}
        />
        {children}
      </div>
    </div>
  );
};
